import { moveToFulfilled } from '@ms/yammer-data-support';
import { Reducer } from 'redux';

import {
  FeedLoadFulfilledAction,
  FeedLoadNewerThreadsFulfilledAction,
  FeedLoadOlderThreadsFulfilledAction,
} from '../../domains/feedLoad/actions.types';
import {
  NestedThreadDeepLinkSecondLevelLoadFulfilledAction,
  NestedThreadDeepLinkTopLevelLoadFulfilledAction,
  NestedThreadLoadFulfilledAction,
} from '../../domains/nestedThread/types';
import { NestedThreadListLoadFulfilledAction } from '../../domains/nestedThreadList/actions.types';
import {
  ThreadPinToEventFeedFulfilledAction,
  ThreadPinToGroupFeedFulfilledAction,
  ThreadPinToTeamsMeetingFeedFulfilledAction,
  ThreadUnpinFromEventFeedFulfilledAction,
  ThreadUnpinFromGroupFeedFulfilledAction,
  ThreadUnpinFromTeamsMeetingFeedFulfilledAction,
} from '../../domains/pinnedThreads/actions.types';
import {
  PublishDirectMessageFulfilledAction,
  PublishEditMessageFulfilledAction,
  PublishEventMessageFulfilledAction,
  PublishGroupMessageFulfilledAction,
  PublishPollMessageFulfilledAction,
  PublishPraiseMessageFulfilledAction,
  PublishReplyMessageFulfilledAction,
  PublishTeamsMeetingMessageFulfilledAction,
  PublishUserMessageFulfilledAction,
  PublishUserMomentMessageFulfilledAction,
  PublishUserPollMessageFulfilledAction,
  PublishUserPraiseMessageFulfilledAction,
} from '../../domains/publishers/actions.types';
import { SearchLoadFulfilledAction, SearchLoadMoreFulfilledAction } from '../../domains/searches/actions.types';
import {
  SecondLevelRepliesLoadLastFulfilledAction,
  SecondLevelRepliesLoadNextFulfilledAction,
  SecondLevelRepliesLoadPreviousFulfilledAction,
} from '../../domains/secondLevelReplies/actions.types';
import {
  ThreadLoadFulfilledAction,
  ThreadLoadNewerRepliesFulfilledAction,
  ThreadLoadOlderRepliesFulfilledAction,
} from '../../domains/threadLoad/actions.types';
import { ThreadSharesLoadFulfilledAction } from '../../domains/threadShares/actions.types';
import {
  TopLevelRepliesLoadInitialFulfilledAction,
  TopLevelRepliesLoadLastFulfilledAction,
  TopLevelRepliesLoadNextFulfilledAction,
  TopLevelRepliesLoadPreviousFulfilledAction,
} from '../../domains/topLevelReplies/actions.types';

import { AnonymousUsers } from './types';

export type AnonymousUserAction =
  | FeedLoadFulfilledAction
  | FeedLoadNewerThreadsFulfilledAction
  | FeedLoadOlderThreadsFulfilledAction
  | SearchLoadFulfilledAction
  | SearchLoadMoreFulfilledAction
  | ThreadLoadFulfilledAction
  | ThreadLoadNewerRepliesFulfilledAction
  | ThreadLoadOlderRepliesFulfilledAction
  | ThreadPinToEventFeedFulfilledAction
  | ThreadPinToGroupFeedFulfilledAction
  | ThreadPinToTeamsMeetingFeedFulfilledAction
  | ThreadUnpinFromEventFeedFulfilledAction
  | ThreadUnpinFromGroupFeedFulfilledAction
  | ThreadUnpinFromTeamsMeetingFeedFulfilledAction
  | PublishEventMessageFulfilledAction
  | PublishGroupMessageFulfilledAction
  | PublishUserMessageFulfilledAction
  | PublishUserMomentMessageFulfilledAction
  | PublishUserPollMessageFulfilledAction
  | PublishUserPraiseMessageFulfilledAction
  | PublishDirectMessageFulfilledAction
  | PublishEditMessageFulfilledAction
  | PublishReplyMessageFulfilledAction
  | PublishPollMessageFulfilledAction
  | PublishPraiseMessageFulfilledAction
  | PublishTeamsMeetingMessageFulfilledAction
  | NestedThreadLoadFulfilledAction
  | NestedThreadDeepLinkTopLevelLoadFulfilledAction
  | NestedThreadDeepLinkSecondLevelLoadFulfilledAction
  | NestedThreadListLoadFulfilledAction
  | TopLevelRepliesLoadInitialFulfilledAction
  | TopLevelRepliesLoadLastFulfilledAction
  | TopLevelRepliesLoadPreviousFulfilledAction
  | TopLevelRepliesLoadNextFulfilledAction
  | SecondLevelRepliesLoadPreviousFulfilledAction
  | SecondLevelRepliesLoadNextFulfilledAction
  | SecondLevelRepliesLoadLastFulfilledAction
  | ThreadSharesLoadFulfilledAction;

const initialState: AnonymousUsers = {
  pending: { byId: {} },
  fulfilled: { byId: {} },
  rejected: { byId: {} },
};

const reducer: Reducer<AnonymousUsers, AnonymousUserAction> = (state = initialState, action) => {
  switch (action.type) {
    case 'FEED.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.anonymousUsers);
    case 'FEED.LOAD_NEWER_THREADS_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.anonymousUsers);
    case 'FEED.LOAD_OLDER_THREADS_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.anonymousUsers);
    case 'SEARCH.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.anonymousUsers);
    case 'SEARCH.LOAD_MORE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.anonymousUsers);
    case 'THREAD.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.anonymousUsers);
    case 'THREAD.LOAD_NEWER_REPLIES_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.anonymousUsers);
    case 'THREAD.LOAD_OLDER_REPLIES_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.anonymousUsers);
    case 'THREAD.PIN_TO_EVENT_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.anonymousUsers);
    case 'THREAD.PIN_TO_GROUP_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.anonymousUsers);
    case 'THREAD.PIN_TO_TEAMS_MEETING_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.anonymousUsers);
    case 'THREAD.UNPIN_FROM_EVENT_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.anonymousUsers);
    case 'THREAD.UNPIN_FROM_GROUP_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.anonymousUsers);
    case 'THREAD.UNPIN_FROM_TEAMS_MEETING_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.anonymousUsers);
    case 'PUBLISHER.PUBLISH_EVENT_MESSAGE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.anonymousUsers);
    case 'PUBLISHER.PUBLISH_GROUP_MESSAGE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.anonymousUsers);
    case 'PUBLISHER.PUBLISH_USER_MESSAGE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.anonymousUsers);
    case 'PUBLISHER.PUBLISH_USER_MOMENT_MESSAGE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.anonymousUsers);
    case 'PUBLISHER.PUBLISH_USER_POLL_MESSAGE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.anonymousUsers);
    case 'PUBLISHER.PUBLISH_USER_PRAISE_MESSAGE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.anonymousUsers);
    case 'PUBLISHER.PUBLISH_DIRECT_MESSAGE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.anonymousUsers);
    case 'PUBLISHER.PUBLISH_EDIT_MESSAGE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.anonymousUsers);
    case 'PUBLISHER.PUBLISH_REPLY_MESSAGE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.anonymousUsers);
    case 'PUBLISHER.PUBLISH_POLL_MESSAGE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.anonymousUsers);
    case 'PUBLISHER.PUBLISH_PRAISE_MESSAGE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.anonymousUsers);
    case 'PUBLISHER.PUBLISH_TEAMS_MEETING_MESSAGE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.anonymousUsers);
    case 'NESTED_THREAD.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.anonymousUsers);
    case 'NESTED_THREAD.LOAD_DEEP_LINK_TOP_LEVEL_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.anonymousUsers);
    case 'NESTED_THREAD.LOAD_DEEP_LINK_SECOND_LEVEL_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.anonymousUsers);
    case 'NESTED_THREAD_LIST.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.anonymousUsers);
    case 'TOP_LEVEL_REPLIES.LOAD_INITIAL_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.anonymousUsers);
    case 'TOP_LEVEL_REPLIES.LOAD_PREVIOUS_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.anonymousUsers);
    case 'TOP_LEVEL_REPLIES.LOAD_NEXT_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.anonymousUsers);
    case 'TOP_LEVEL_REPLIES.LOAD_LAST_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.anonymousUsers);
    case 'SECOND_LEVEL_REPLIES.LOAD_PREVIOUS_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.anonymousUsers);
    case 'SECOND_LEVEL_REPLIES.LOAD_NEXT_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.anonymousUsers);
    case 'SECOND_LEVEL_REPLIES.LOAD_LAST_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.anonymousUsers);
    case 'THREAD_SHARES.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.anonymousUsers);
    default:
      return state;
  }
};

export default reducer;
